/**
 * Utility for resolving the `__relay_internal__pv__*` provided variables
 * required by the search-results persisted query.
 *
 * Non-Relay callers (e.g. SSR preload helpers that POST persisted queries
 * directly to AGG) must include these variables in their request body to
 * satisfy the gateway's `Boolean!` NonNull declarations. Historically those
 * callers duplicated the keys as a static map, which silently drifts from
 * the live `.relayprovider` evaluations performed inside the Relay
 * environment.
 *
 * This module is the single source of truth for that resolution. It calls
 * each provider's `get()` at invocation time so feature-gate / experiment
 * results are picked up at runtime (including during SSR, on the
 * assumption that the underlying feature gates are SSR-evaluable). The
 * shape of the returned map is pinned to the generated query artifact by
 * `index.relay-provided-variables.test.ts`.
 *
 * SSR Safety: This function is synchronous, contains no React hooks, and
 * does not touch browser-only globals directly. Each provider's `get()`
 * implementation must uphold the same contract.
 */

import isAssetsEnabledProvider from '../../ui/search-page-relay/search-results/is-assets-enabled.relayprovider';
import isLeadNounEnabledProvider from '../../ui/search-page-relay/search-results/search-result/search-result-renderer/search-graph-result/search-graph-lead/is-lead-noun-enabled.relayprovider';
import isLeadUserAsTitleEnabledProvider from '../../ui/search-page-relay/search-results/search-result/search-result-renderer/search-graph-result/search-graph-lead/is-lead-user-as-title-enabled.relayprovider';
import isSlackTopReactionEnabledProvider from '../../ui/search-page-relay/search-results/search-result/search-result-renderer/search-graph-result/search-graph-message/is-slack-top-reaction-enabled.relayprovider';
import isSlackWorkspaceMetadataEnabledProvider from '../../ui/search-page-relay/search-results/search-result/search-result-renderer/search-graph-result/search-graph-message/is-slack-workspace-metadata-enabled.relayprovider';
import isProjectRendererEnabledProvider from '../../ui/search-page-relay/search-results/search-result/search-result-renderer/search-graph-result/search-graph-project/is-project-renderer-enabled.relayprovider';

import type { RelayProvidedVariables } from './types';

/**
 * Returns the `__relay_internal__pv__*` provided variables for the
 * search-results persisted query, with each value evaluated against its
 * underlying feature gate at call time.
 *
 * Callers POSTing the persisted query directly to AGG should merge the
 * result into their request body. The keys MUST stay in lockstep with
 * `params.providedVariables` on the generated `searchResultsAsyncQuery`
 * artifact — this parity is enforced by
 * `index.relay-provided-variables.test.ts`.
 */
export const getSearchResultsQueryRelayProvidedVariables = (): RelayProvidedVariables => ({
	__relay_internal__pv__isassetsenabledrelayprovider: isAssetsEnabledProvider.get(),
	__relay_internal__pv__isleadnounenabledrelayprovider: isLeadNounEnabledProvider.get(),
	__relay_internal__pv__isleaduserastitleenabledrelayprovider:
		isLeadUserAsTitleEnabledProvider.get(),
	__relay_internal__pv__isprojectrendererenabledrelayprovider:
		isProjectRendererEnabledProvider.get(),
	__relay_internal__pv__isslacktopreactionenabledrelayprovider:
		isSlackTopReactionEnabledProvider.get(),
	__relay_internal__pv__isslackworkspacemetadataenabledrelayprovider:
		isSlackWorkspaceMetadataEnabledProvider.get(),
});
